import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const EditItemModal = ({ isOpen, onClose, onUpdateItem, item }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sku: '',
    category: '',
    supplier: '',
    unitPrice: '',
    currentStock: '',
    reorderPoint: '',
    trackInventory: true,
    isActive: true
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categoryOptions = [
    { value: 'Electronics', label: 'Electronics' },
    { value: 'Office Supplies', label: 'Office Supplies' },
    { value: 'Raw Materials', label: 'Raw Materials' },
    { value: 'Packaging Materials', label: 'Packaging Materials' }
  ];

  const supplierOptions = [
    { value: 'TechCorp Solutions', label: 'TechCorp Solutions' },
    { value: 'Office Pro Ltd', label: 'Office Pro Ltd' },
    { value: 'Material Masters Inc', label: 'Material Masters Inc' },
    { value: 'Eco Materials Co', label: 'Eco Materials Co' }
  ];

  // Populate form when item changes
  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || '',
        description: item.description || '',
        sku: item.sku || '',
        category: item.category || '',
        supplier: item.supplier || '',
        unitPrice: item.unitPrice?.toString() || '',
        currentStock: item.currentStock?.toString() || '',
        reorderPoint: item.reorderPoint?.toString() || '',
        trackInventory: item.trackInventory ?? true,
        isActive: item.isActive ?? true
      });
    }
  }, [item]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Item name is required';
    }

    if (!formData.sku?.trim()) {
      newErrors.sku = 'SKU is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (!formData.supplier) {
      newErrors.supplier = 'Supplier is required';
    }

    if (!formData.unitPrice || parseFloat(formData.unitPrice) <= 0) {
      newErrors.unitPrice = 'Valid unit price is required';
    }

    if (!formData.currentStock || parseInt(formData.currentStock) < 0) {
      newErrors.currentStock = 'Valid current stock is required';
    }

    if (!formData.reorderPoint || parseInt(formData.reorderPoint) < 0) {
      newErrors.reorderPoint = 'Valid reorder point is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));

      const currentStock = parseInt(formData.currentStock);
      const reorderPoint = parseInt(formData.reorderPoint);

      // Determine status based on stock levels
      let status = 'in-stock';
      if (currentStock === 0) {
        status = 'out-of-stock';
      } else if (currentStock <= reorderPoint) {
        status = 'low-stock';
      } else if (currentStock > reorderPoint * 3) {
        status = 'overstock';
      }

      const updatedItem = {
        ...item,
        name: formData.name,
        description: formData.description,
        sku: formData.sku,
        category: formData.category,
        supplier: formData.supplier,
        unitPrice: parseFloat(formData.unitPrice),
        currentStock: currentStock,
        reorderPoint: reorderPoint,
        status: status,
        trackInventory: formData.trackInventory,
        isActive: formData.isActive,
        updatedAt: new Date().toISOString()
      };

      onUpdateItem(updatedItem);
      handleClose();
    } catch (error) {
      console.error('Error updating item:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  if (!isOpen || !item) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-500 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Edit" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Edit Item</h2>
              <p className="text-sm text-muted-foreground">Update inventory item details</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            iconName="X"
          />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Basic Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Item Name"
                type="text"
                placeholder="Enter item name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={errors.name}
                required
                className="md:col-span-2"
              />

              <Input
                label="Description"
                type="text"
                placeholder="Enter item description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="md:col-span-2"
              />

              <Input
                label="SKU"
                type="text"
                placeholder="Enter SKU"
                value={formData.sku}
                onChange={(e) => handleInputChange('sku', e.target.value)}
                error={errors.sku}
                required
              />

              <Select
                label="Category"
                placeholder="Select category"
                options={categoryOptions}
                value={formData.category}
                onChange={(value) => handleInputChange('category', value)}
                error={errors.category}
                required
              />
            </div>
          </div>

          {/* Supplier & Pricing */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Supplier & Pricing</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Supplier"
                placeholder="Select supplier"
                options={supplierOptions}
                value={formData.supplier}
                onChange={(value) => handleInputChange('supplier', value)}
                error={errors.supplier}
                required
              />

              <Input
                label="Unit Price"
                type="number"
                placeholder="0.00"
                value={formData.unitPrice}
                onChange={(e) => handleInputChange('unitPrice', e.target.value)}
                error={errors.unitPrice}
                min="0"
                step="0.01"
                required
              />
            </div>
          </div>

          {/* Inventory Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Inventory Details</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Current Stock"
                type="number"
                placeholder="0"
                value={formData.currentStock}
                onChange={(e) => handleInputChange('currentStock', e.target.value)}
                error={errors.currentStock}
                min="0"
                required
              />

              <Input
                label="Reorder Point"
                type="number"
                placeholder="0"
                value={formData.reorderPoint}
                onChange={(e) => handleInputChange('reorderPoint', e.target.value)}
                error={errors.reorderPoint}
                min="0"
                required
              />
            </div>
          </div>

          {/* Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Settings</h3>
            
            <div className="space-y-3">
              <Checkbox
                label="Track inventory for this item"
                description="Enable automatic stock tracking and alerts"
                checked={formData.trackInventory}
                onChange={(e) => handleInputChange('trackInventory', e.target.checked)}
              />

              <Checkbox
                label="Item is active"
                description="Active items are available for transactions"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              iconName="Save"
              iconPosition="left"
            >
              Update Item
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditItemModal;
